import { Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { createEffectFactory } from '@recruitee/ngrx';
import { OfferKind } from '@recruitee/offers-types';

import { TalentPoolsHttpClient } from './talent-pool.http-client';
import { TalentPoolsNewActions } from './talent-pool.store';

@Injectable({ providedIn: 'root' })
export class TalentPoolsEffects {
  public effects = createEffectFactory(TalentPoolsNewActions, this.actions$);

  public fetchOne$ = this.effects(TalentPoolsNewActions.fetchOne, ({ payload }) =>
    this.http.fetchOne(payload.id).pipe(
      map(response => ({
        id: payload.id,
        talentPool: {
          ...response.talentPool,
          kind: OfferKind.TalentPool,
        },
      })),
    ),
  );

  public fetchCollection$ = this.effects(
    TalentPoolsNewActions.fetchCollection,
    action =>
      this.http.fetchCollection(action.payload.scope).pipe(
        map(response => ({
          data: response.talentPools.map(item => ({
            ...item,
            kind: OfferKind.TalentPool,
          })),
        })),
      ),
    { customMapOperator: mergeMap },
  );

  public update$ = this.effects(TalentPoolsNewActions.updateOnePessimistic, ({ payload }) =>
    this.http.update(payload.data).pipe(
      map(response => ({
        id: response.talentPool.id,
        data: {
          ...response.talentPool,
          kind: OfferKind.TalentPool,
        },
      })),
      catchError(error => of({ id: payload.data.id, error })),
    ),
  );

  public create$ = this.effects(TalentPoolsNewActions.createOnePessimistic, ({ payload }) =>
    this.http
      .create(payload.data)
      .pipe(map(response => ({ data: { ...response.talentPool, kind: OfferKind.TalentPool } }))),
  );

  public duplicate$ = this.effects(TalentPoolsNewActions.duplicate, action =>
    this.http.duplicate(action.payload.data.id).pipe(
      map(response =>
        TalentPoolsNewActions.duplicateSuccess({
          payload: {
            data: {
              ...response.talentPool,
              kind: OfferKind.TalentPool,
            },
          },
        }),
      ),
      catchError(error => of(TalentPoolsNewActions.duplicateFailed({ payload: error }))),
    ),
  );

  public delete$ = this.effects(TalentPoolsNewActions.removeOnePessimistic, action =>
    this.http.delete(action.payload.data).pipe(map(response => ({ data: response.talentPool }))),
  );

  public archive$ = this.effects(TalentPoolsNewActions.archive, action =>
    this.http.archive(action.payload.id).pipe(
      map(response =>
        TalentPoolsNewActions.archiveSuccess({
          payload: {
            data: {
              ...response.talentPool,
              kind: OfferKind.TalentPool,
            },
          },
        }),
      ),
      catchError(error => of(TalentPoolsNewActions.archiveFailed({ payload: error }))),
    ),
  );

  public retrieve$ = this.effects(TalentPoolsNewActions.retrieve, action =>
    this.http.retrieve(action.payload.id).pipe(
      map(response =>
        TalentPoolsNewActions.retrieveSuccess({
          payload: {
            data: {
              ...response.talentPool,
              kind: OfferKind.TalentPool,
            },
          },
        }),
      ),
      catchError(error => of(TalentPoolsNewActions.retrieveFailed({ payload: error }))),
    ),
  );

  public follow$ = this.effects(TalentPoolsNewActions.follow, action =>
    this.http.follow(action.payload.id).pipe(
      map(response =>
        TalentPoolsNewActions.followSuccess({
          payload: {
            data: {
              ...response.talentPool,
              kind: OfferKind.TalentPool,
            },
          },
        }),
      ),
      catchError(error => of(TalentPoolsNewActions.followFailed({ payload: error }))),
    ),
  );

  public unfollow$ = this.effects(TalentPoolsNewActions.unfollow, action =>
    this.http.unfollow(action.payload.id).pipe(
      map(response =>
        TalentPoolsNewActions.unfollowSuccess({
          payload: {
            data: {
              ...response.talentPool,
              kind: OfferKind.TalentPool,
            },
          },
        }),
      ),
      catchError(error => of(TalentPoolsNewActions.unfollowFailed({ payload: error }))),
    ),
  );

  public updatePositions$ = this.effects(TalentPoolsNewActions.updatePositions, ({ payload }) =>
    this.http.updatePositions({ positions: payload.positions }).pipe(
      switchMap(response => of(TalentPoolsNewActions.updatePositionSuccess({ payload: response }))),
      catchError(error => of(TalentPoolsNewActions.updatePositionsFailed({ payload: { error } }))),
    ),
  );

  constructor(
    private actions$: Actions,
    private http: TalentPoolsHttpClient,
  ) {}
}
